/**
 * PostHog Analytics Integration
 *
 * R14: Using npm package instead of CDN for SRI compliance.
 * The npm package is bundled with the app, eliminating external script loading.
 */

import posthog from 'posthog-js';

// PostHog configuration
const POSTHOG_API_KEY = 'phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE';
const POSTHOG_API_HOST = 'https://us.i.posthog.com';

// Initialize PostHog
let isInitialized = false;

export const initPostHog = () => {
  if (isInitialized) return;

  // Only initialize in production or if explicitly enabled
  if (process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENABLE_POSTHOG === 'true') {
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_API_HOST,
      person_profiles: 'identified_only',
      capture_pageview: false, // We'll handle this manually
      capture_pageleave: true,
      autocapture: false, // Disable autocapture for privacy
      disable_session_recording: false,
      loaded: (ph) => {
        // Disable in development unless explicitly enabled
        if (process.env.NODE_ENV !== 'production' && process.env.REACT_APP_ENABLE_POSTHOG !== 'true') {
          ph.opt_out_capturing();
        }
      }
    });
    isInitialized = true;
  }
};

/**
 * Capture a custom event
 * @param {string} event - Event name
 * @param {object} props - Event properties
 */
export const captureEvent = (event, props = {}) => {
  if (!isInitialized) {
    initPostHog();
  }

  try {
    posthog.capture(event, props);
  } catch (e) {
    // Silently fail - analytics should not break the app
    console.debug('PostHog capture failed:', e);
  }
};

/**
 * Identify a user
 * @param {string} userId - Unique user identifier
 * @param {object} traits - User traits/properties
 */
export const identifyUser = (userId, traits = {}) => {
  if (!isInitialized) {
    initPostHog();
  }

  try {
    posthog.identify(userId, traits);
  } catch (e) {
    console.debug('PostHog identify failed:', e);
  }
};

/**
 * Reset user identity (on logout)
 */
export const resetUser = () => {
  try {
    posthog.reset();
  } catch (e) {
    console.debug('PostHog reset failed:', e);
  }
};

/**
 * Capture page view
 * @param {string} pageName - Name of the page
 */
export const capturePageView = (pageName) => {
  captureEvent('$pageview', { page: pageName });
};

// Export the posthog instance for advanced usage
export { posthog };

// Auto-initialize on import
initPostHog();
